import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
// UPPDRAG-51: Volleyball icon as the ball-holder badge mark. Lucide
// doesn't ship a tennis-ball icon; Volleyball reads as "ball" clearly
// enough for our purpose.
import { CalendarDays, Clock, MapPin, MessageCircle, Volleyball } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useState } from "react";
import ReportResult from "./ReportResult";
import ConfirmResult from "./ConfirmResult";
import MatchChat from "./MatchChat";
import { Button } from "@/components/ui/button";
// TASK-67 PR-D: replace teams.player1/2 reads with team_members embed +
// pickFirstTwoPrimaries for the canonical "first 2 primaries by joined_at"
// pair.
import { pickFirstTwoPrimaries } from "@/lib/teamHelpers";
// MEGA-SPRINT Sprint 4 PR 4.1 — dual-identity team_member display.
import { resolveTeamMember } from "@/lib/playerDisplay";
// PR-1d: dual-identity ball-holder display resolver. Reads either
// ball_holder_user_id (profile-linked) or ball_holder_customer_id
// (CRM-only customer) via the mutex-enforced dual FK pattern from PR-1a.
import { resolveBallHolderFromEmbed, type BallHolderEmbed } from "@/lib/ballHolderDisplay";

interface MatchDetailProps {
  matchId: string;
  teamId: string;
  league: Record<string, unknown>;
  onClose: () => void;
}

const statusLabels: Record<string, string> = {
  scheduled: "Kommande",
  rescheduled: "Ombokad",
  completed: "Spelad",
  walkover_home: "WO Hemma",
  walkover_away: "WO Borta",
  cancelled: "Inställd",
};

export default function MatchDetail({ matchId, teamId, league, onClose }: MatchDetailProps) {
  const { user } = useAuth();
  const [showReport, setShowReport] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "chat">("details");

  const { data: match, isLoading } = useQuery({
    queryKey: ["match-detail", matchId],
    queryFn: async () => {
      // UPPDRAG-51: embed the ball-holder so the badge below can render
      // the player's full_name without an extra round-trip.
      // PR-1d: dual-FK embed — ball_holder_user (profile) OR
      // ball_holder_customer (CRM customer) per the mutex-enforced
      // schema established in PR-1a. The renderer below resolves the
      // pair via `resolveBallHolderFromEmbed`.
      // TASK-67 PR-D: replace dual player1/player2 PostgREST embeds with a
      // single team_members embed (role + joined_at + profile via
      // team_members_user_id_fkey). Renderer derives the canonical
      // "first 2 primaries" pair via pickFirstTwoPrimaries below.
      const { data, error } = await (supabase as any)
        .from("league_matches")
        .select(`
          *,
          home_team:teams!league_matches_home_team_id_fkey(
            id, team_name,
            members:team_members(user_id, customer_id, role, joined_at,
              profile:profiles!team_members_user_id_fkey(full_name, level),
              customer:customers!team_members_customer_id_fkey(full_name, email))
          ),
          away_team:teams!league_matches_away_team_id_fkey(
            id, team_name,
            members:team_members(user_id, customer_id, role, joined_at,
              profile:profiles!team_members_user_id_fkey(full_name, level),
              customer:customers!team_members_customer_id_fkey(full_name, email))
          ),
          court:courts(name),
          ball_holder_user:profiles!league_matches_ball_holder_user_id_fkey(full_name),
          ball_holder_customer:customers!league_matches_ball_holder_customer_id_fkey(full_name),
          league:leagues!league_matches_league_id_fkey(ball_holder_principle)
        `)
        .eq("id", matchId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  // Chat unread count
  const { data: chatCount = 0 } = useQuery({
    queryKey: ["match-chat-count", matchId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("match_chat_messages")
        .select("id", { count: "exact", head: true })
        .eq("match_id", matchId);
      if (error) return 0;
      return count || 0;
    },
  });

  if (!match && !isLoading) {
    onClose();
    return null;
  }

  // TASK-67 PR-D: helpers below now resolve the player pair via
  // pickFirstTwoPrimaries on the team_members embed (sorted by joined_at).
  type EmbeddedMember = {
    user_id: string | null;
    customer_id: string | null;
    role: "primary" | "reserve";
    joined_at: string;
    profile: { full_name: string | null; level: number | null } | null;
    customer: { full_name: string | null; email: string | null } | null;
  };
  type EmbeddedTeam = {
    id?: string;
    team_name?: string | null;
    members?: EmbeddedMember[] | null;
  };

  // Sprint 4 PR 4.1: resolve display name via dual-identity helper so
  // customer-only team members render with their full_name (or email
  // fallback) instead of "?".
  const memberDisplayName = (m: EmbeddedMember | null): string => {
    if (!m) return "?";
    const r = resolveTeamMember(m);
    return r.kind === "none" ? "?" : r.displayName;
  };

  const getTeamLabel = (team: Record<string, unknown> | null) => {
    if (!team) return "—";
    const t = team as EmbeddedTeam;
    if (t.team_name) return t.team_name;
    const [p1, p2] = pickFirstTwoPrimaries(t.members ?? null);
    return `${memberDisplayName(p1)} / ${memberDisplayName(p2)}`;
  };

  const getPlayerInfo = (team: Record<string, unknown> | null) => {
    if (!team) return [];
    const t = team as EmbeddedTeam;
    const [p1, p2] = pickFirstTwoPrimaries(t.members ?? null);
    return [
      { name: memberDisplayName(p1), level: p1?.profile?.level ?? null },
      { name: memberDisplayName(p2), level: p2?.profile?.level ?? null },
    ];
  };

  const result = match?.result as Record<string, unknown> | null;
  const isReportable = match && (match.status === "scheduled" || match.status === "rescheduled");

  const needsConfirmation = (() => {
    if (!match || match.status !== "completed" || !match.reported_by || match.confirmed_by) return false;
    const opponent = match.home_team_id === teamId ? match.away_team : match.home_team;
    if (!opponent) return false;
    // TASK-67 PR-D: opponent reporter check now walks team_members
    // (role='primary') user_ids instead of opponent.player1/2_id slots.
    const oppMembers = ((opponent as EmbeddedTeam).members ?? [])
      .filter((m) => m.role === "primary" && m.user_id)
      .map((m) => m.user_id as string);
    return oppMembers.includes(match.reported_by as string);
  })();

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto">
        {isLoading || !match ? (
          <div className="py-10 text-center text-muted-foreground">Laddar...</div>
        ) : showReport ? (
          <ReportResult
            match={match}
            teamId={teamId}
            league={league}
            onClose={() => setShowReport(false)}
            onSuccess={() => setShowReport(false)}
          />
        ) : showConfirm ? (
          <ConfirmResult
            match={match}
            teamId={teamId}
            league={league}
            onClose={() => setShowConfirm(false)}
            onSuccess={() => setShowConfirm(false)}
          />
        ) : (
          <>
            <SheetHeader>
              <SheetTitle className="text-left">Matchdetaljer</SheetTitle>
            </SheetHeader>

            {/* Tab navigation */}
            <div className="flex gap-1 mt-3 border-b">
              <button
                onClick={() => setActiveTab("details")}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === "details"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                Detaljer
              </button>
              <button
                onClick={() => setActiveTab("chat")}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                  activeTab === "chat"
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <MessageCircle className="h-4 w-4" />
                Chatt
                {chatCount > 0 && (
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 min-w-[1rem]">
                    {chatCount}
                  </Badge>
                )}
              </button>
            </div>

            {activeTab === "chat" ? (
              <div className="mt-2">
                <MatchChat matchId={matchId} />
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                {/* Teams */}
                <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center">
                  <div className="text-center">
                    <p className="font-semibold text-sm">{getTeamLabel(match.home_team)}</p>
                    {getPlayerInfo(match.home_team).map((p, i) => (
                      <p key={i} className="text-xs text-muted-foreground">
                        {p.name} {p.level ? `(${p.level})` : ""}
                      </p>
                    ))}
                  </div>
                  <span className="text-lg font-bold text-muted-foreground">vs</span>
                  <div className="text-center">
                    <p className="font-semibold text-sm">{getTeamLabel(match.away_team)}</p>
                    {getPlayerInfo(match.away_team).map((p, i) => (
                      <p key={i} className="text-xs text-muted-foreground">
                        {p.name} {p.level ? `(${p.level})` : ""}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="flex justify-center gap-2 flex-wrap">
                  <Badge variant="secondary">
                    {statusLabels[match.status] || match.status}
                  </Badge>
                  {/* UPPDRAG-51 + PR-1d: ball-holder badge (dual-FK).
                       - principle = club_provides → "Klubben" badge always
                       - resolved kind: 'user' or 'customer' → render
                         player's displayName via the helper (handles
                         dual-FK + Spelare fallback)
                       - principle missing OR resolved kind 'none' and
                         not club_provides → render nothing (legacy match
                         pre-UPPDRAG-51, or no eligible holder picked) */}
                  {(() => {
                    const row = match as BallHolderEmbed & {
                      league?: { ball_holder_principle?: string } | null;
                    };
                    const principle = row.league?.ball_holder_principle;
                    const resolved = resolveBallHolderFromEmbed(row);
                    if (principle === "club_provides") {
                      return (
                        <Badge variant="outline" className="gap-1">
                          <Volleyball className="h-3 w-3" />
                          Bollhållare: Klubben
                        </Badge>
                      );
                    }
                    if (resolved.kind === "user" || resolved.kind === "customer") {
                      return (
                        <Badge variant="outline" className="gap-1">
                          <Volleyball className="h-3 w-3" />
                          Bollhållare: {resolved.displayName}
                        </Badge>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Meta */}
                <div className="flex flex-col items-center gap-1 text-sm text-muted-foreground">
                  <div className="flex gap-4">
                    {match.scheduled_date && (
                      <span className={`flex items-center gap-1 ${match.rescheduled_date ? "line-through opacity-50" : ""}`}>
                        <CalendarDays className="h-4 w-4" />
                        {format(new Date(match.scheduled_date), "d MMM yyyy", { locale: sv })}
                      </span>
                    )}
                    {match.scheduled_time && (
                      <span className={`flex items-center gap-1 ${match.rescheduled_time ? "line-through opacity-50" : ""}`}>
                        <Clock className="h-4 w-4" />
                        {match.scheduled_time.slice(0, 5)}
                      </span>
                    )}
                    {((match as { court?: { name?: string } }).court?.name) && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {(match as { court?: { name?: string } }).court?.name}
                      </span>
                    )}
                  </div>
                  {match.rescheduled_date && (
                    <div className="flex gap-4 text-primary font-medium">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="h-4 w-4" />
                        {format(new Date(match.rescheduled_date), "d MMM yyyy", { locale: sv })}
                      </span>
                      {match.rescheduled_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {(match.rescheduled_time as string).slice(0, 5)}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Result */}
                {result?.sets && result.sets.length > 0 && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-center">Resultat</p>
                      <div className="flex justify-center gap-4">
                        {result.sets.map((set: Record<string, unknown>, i: number) => (
                          <div key={i} className="text-center">
                            <p className="text-xs text-muted-foreground">Set {i + 1}</p>
                            <p className="text-lg font-bold">
                              {set.home}-{set.away}
                            </p>
                            {set.tiebreak && (
                              <p className="text-xs text-muted-foreground">
                                TB: {set.tiebreak_home}-{set.tiebreak_away}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                      {result.points_home !== undefined && (
                        <p className="text-center text-sm text-muted-foreground">
                          Poäng: {result.points_home} – {result.points_away}
                        </p>
                      )}
                    </div>
                  </>
                )}

                {/* Actions */}
                <Separator />
                <div className="flex gap-2 justify-center">
                  {isReportable && (
                    <Button onClick={() => setShowReport(true)}>
                      Rapportera resultat
                    </Button>
                  )}
                  {needsConfirmation && (
                    <Button variant="outline" onClick={() => setShowConfirm(true)}>
                      Bekräfta resultat
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
